import { AxiosResponse } from "axios";

import {
  IAdminPatientDetail,
  IAdminPatientListItem,
  IAdminUserDetail,
  IArchivedPatientListItem,
  IPatientAction,
  IPatientCondition,
  IPatientMedication,
  IPatientSurveyWithAnswers,
  IPatientTriggeredAlert,
  IUpdatePatientRequest,
  IUpdateUserRequest,
} from "../../@types/admin/AdminPatient";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { api } from "../api";

/**
 * Patients admin page (EN-5169), migrated from the Retool Patients app.
 *
 * Every endpoint here already existed. Retool ran five direct-DB queries on
 * this page (countries, condition types, condition details, baselines,
 * measurements); four were already served unchanged and the fifth only lacked
 * a patient filter. See `portal/retool-migration.md`.
 */
/** Sort keys `GET /Retool/Patients` accepts (EN-5277), the API's enum names. */
export type IAdminPatientSortBy =
  | "Id"
  | "Name"
  | "Email"
  | "Mrn"
  | "Created"
  | "Hospital"
  | "Ward"
  | "LastUsed"
  | "Archived";

/** Filters shared by both patient lists (EN-5277). */
interface IPatientListFilters {
  page: number;
  pageSize: number;
  search?: string;
  /** Hospital GROUP id. */
  hospitalGroupId?: number;
  /** Ward membership (`PatientWards`), not only the primary ward. */
  wardId?: number;
}

export interface IAdminPatientsQuery extends IPatientListFilters {
  sortBy?: IAdminPatientSortBy;
  sortDescending?: boolean;
}

export type IArchivedPatientsQuery = IPatientListFilters;

export const patientsService = {
  /**
   * The patient list. Server-paginated and server-sorted (EN-5277): sorting
   * in the browser reordered only the page on screen.
   */
  async getPatients({
    page,
    pageSize,
    search,
    hospitalGroupId,
    wardId,
    sortBy,
    sortDescending,
  }: IAdminPatientsQuery): Promise<
    AxiosResponse<IPaginatedResponse<IAdminPatientListItem[]>>
  > {
    return api.get<IPaginatedResponse<IAdminPatientListItem[]>>(
      "/Retool/Patients",
      // Empty filters are dropped, which keeps the URL and the query key
      // honest about whether anything is being filtered on.
      {
        params: {
          page,
          pageSize,
          search: search || undefined,
          hospitalGroupId,
          wardId,
          sortBy,
          sortDescending: sortDescending || undefined,
        },
      }
    );
  },

  /**
   * Discharged patients (EN-5171). Server-paginated with the same
   * server-side search and hospital/ward filters as the live list — the
   * Retool archived page had no search box at all.
   */
  async getArchivedPatients({
    page,
    pageSize,
    search,
    hospitalGroupId,
    wardId,
  }: IArchivedPatientsQuery): Promise<
    AxiosResponse<IPaginatedResponse<IArchivedPatientListItem[]>>
  > {
    return api.get<IPaginatedResponse<IArchivedPatientListItem[]>>(
      "/Retool/Patients/Archived",
      {
        params: {
          page,
          pageSize,
          search: search || undefined,
          hospitalGroupId,
          wardId,
        },
      }
    );
  },

  async getPatient(
    patientId: number
  ): Promise<AxiosResponse<IAdminPatientDetail>> {
    return api.get<IAdminPatientDetail>(`/Retool/Patients/${patientId}`);
  },

  async updatePatient(
    patientId: number,
    request: IUpdatePatientRequest
  ): Promise<AxiosResponse<IAdminPatientDetail>> {
    return api.put<IAdminPatientDetail>(
      `/Retool/Patients/${patientId}`,
      request
    );
  },

  /** The patient's linked login. */
  async getUser(userId: number): Promise<AxiosResponse<IAdminUserDetail>> {
    return api.get<IAdminUserDetail>(`/Retool/Users/${userId}`);
  },

  async updateUser(
    userId: number,
    request: IUpdateUserRequest
  ): Promise<AxiosResponse<IAdminUserDetail>> {
    return api.put<IAdminUserDetail>(`/Retool/Users/${userId}`, request);
  },

  /**
   * The patient's conditions. Retool read `cms_condition` directly
   * (`getConditionDetails.sql`); `GET /Retool/Conditions` already returns the
   * same columns plus the diagnosis dates its query left out.
   */
  async getPatientConditions(
    patientId: number
  ): Promise<AxiosResponse<IPaginatedResponse<IPatientCondition[]>>> {
    return api.get<IPaginatedResponse<IPatientCondition[]>>(
      "/Retool/Conditions",
      { params: { patientId, page: 1, pageSize: 200 } }
    );
  },

  /**
   * Changes the patient's condition.
   *
   * ⚠️ Clinically significant: the condition decides what the patient app
   * shows and which workflows evaluate. Always behind a confirmation.
   */
  async changeCondition(
    patientId: number,
    conditionTypeId: number
  ): Promise<AxiosResponse<unknown>> {
    return api.post(`/Retool/Patients/${patientId}/ChangeCondition`, {
      conditionTypeId,
    });
  },

  /** Resets the patient's password. Takes the USER id, not the patient id. */
  async changePassword(
    userId: number,
    newPassword: string
  ): Promise<AxiosResponse<unknown>> {
    return api.post(`/Retool/Patients/${userId}/ChangePassword`, {
      newPassword,
    });
  },

  /** Registers the patient on the eMED integration. */
  async sendEmedRegistration(
    patientId: number
  ): Promise<AxiosResponse<unknown>> {
    return api.post(
      `/Retool/Patients/${patientId}/SendEmedPatientRegistration`,
      undefined,
      { params: { archive: false } }
    );
  },

  /**
   * Replaces the patient's ward set.
   *
   * ⚠️ Clinically significant: the ward decides the care team that sees the
   * patient, the card configuration their app shows, and which workflows and
   * alerts evaluate for them (EN-4960 — from ALL their wards, not just the
   * primary). Always behind a confirmation.
   *
   * `wardIds` is the whole new set, not a delta. The first entry becomes the
   * primary ward.
   */
  async changeWards(
    patientId: number,
    wardIds: number[]
  ): Promise<AxiosResponse<unknown>> {
    return api.post(`/Retool/Patients/${patientId}/ChangeWard`, { wardIds });
  },

  /** The patient's protocol actions and when each is next due. */
  async getPatientActions(
    patientId: number
  ): Promise<AxiosResponse<IPatientAction[]>> {
    return api.get<IPatientAction[]>(
      `/Retool/Actions/${patientId}/GetPatientActions`
    );
  },

  /**
   * Alerts that have actually FIRED for this patient — Retool's
   * `modulePatientAlerts`, which is a different module from `moduleAlerts` (the
   * configured-alert list the Alerts page shows).
   */
  async getPatientTriggeredAlerts(
    patientId: number
  ): Promise<AxiosResponse<IPaginatedResponse<IPatientTriggeredAlert[]>>> {
    return api.get<IPaginatedResponse<IPatientTriggeredAlert[]>>(
      "/Retool/TriggeredAlerts",
      { params: { patientId, page: 1, pageSize: 200 } }
    );
  },

  /** The patient's completed surveys, answers included. */
  async getPatientSurveys(
    patientId: number
  ): Promise<AxiosResponse<IPatientSurveyWithAnswers[]>> {
    return api.get<IPatientSurveyWithAnswers[]>(
      "/Retool/Surveys/PatientSurveysWithAnswers",
      { params: { patientId } }
    );
  },

  /**
   * The patient's eMED medication history.
   *
   * A patient-scoped route added in EN-5169. The pre-existing
   * `PatientMedicationHistory` takes `patientId` as an OPTIONAL query param, so
   * a caller allowed to reach it could omit the id and read every patient's
   * medications.
   */
  async getPatientMedications(
    patientId: number
  ): Promise<AxiosResponse<IPatientMedication[]>> {
    return api.get<IPatientMedication[]>(
      `/Retool/EMedIntegration/Patients/${patientId}/MedicationHistory`
    );
  },
};
